"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { STACKMAP_LOGO, stackmapPoster } from "@/lib/v2/stackmap";
import { StackMapFrame } from "./StackMapFrame";

type View = "picture" | "inline" | "full";

const isPhone = () => window.matchMedia("(max-width: 767px)").matches;

/**
 * The 3D model on a project page. A picture loads first and the model only
 * loads on tap, so the page stays fast. Desktop opens it inside the section
 * (with a full screen button); phones open it full screen, because a small
 * 3D box fights with finger scrolling.
 */
export function ProjectStackMapSection({
  slug,
  propertyId,
  propertyName,
}: {
  slug: string;
  propertyId: string;
  propertyName: string;
}) {
  const [view, setView] = useState<View>("picture");

  useEffect(() => {
    if (view !== "full") return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event: KeyboardEvent) => {
      // Escape closes an open form first, not the 3D view behind it.
      if (event.key === "Escape" && !document.querySelector('[role="dialog"]')) setView(isPhone() ? "picture" : "inline");
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [view]);

  return (
    <div>
      <p className="mb-5 max-w-2xl text-base leading-7 text-ink-muted">
        Tap any home to see its view, its afternoon sun and its price range. Sold homes show in grey.{" "}
        <Link href={`/3d-navigator/${slug}`} className="font-semibold text-ink underline decoration-line underline-offset-4">
          Open the full 3D page
        </Link>
      </p>
      {view === "picture" ? (
        <button
          type="button"
          onClick={() => setView(isPhone() ? "full" : "inline")}
          className="group relative block aspect-[4/3] w-full overflow-hidden rounded-2xl border border-line bg-[#e9eef2] md:aspect-[16/7]"
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={stackmapPoster(slug)}
            alt={`3D model of ${propertyName} and the area around it`}
            loading="lazy"
            className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.02]"
          />
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={STACKMAP_LOGO}
            alt="AIPROP 3D"
            className="absolute left-3 top-3 h-10 w-auto rounded-xl bg-white/90 px-3 py-2 shadow-sm md:left-4 md:top-4 md:h-12"
          />
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="rounded-full bg-ink px-6 py-3 text-base font-semibold text-white shadow-[0_10px_30px_rgba(23,29,46,0.35)]">
              Explore in 3D
            </span>
          </span>
        </button>
      ) : (
        <div
          className={
            view === "full"
              ? "fixed inset-0 z-[45] h-[100dvh] bg-[#e9eef2]"
              : "relative h-[640px] w-full overflow-hidden rounded-2xl border border-line bg-[#e9eef2]"
          }
        >
          <StackMapFrame
            slug={slug}
            propertyId={propertyId}
            propertyName={propertyName}
            embed={view === "full" ? "overlay" : "section"}
            place="project_page"
            className="block h-full w-full border-0"
            onExpand={() => setView("full")}
            onClose={() => setView(isPhone() ? "picture" : "inline")}
          />
        </div>
      )}
    </div>
  );
}
